import { FIG_C1, evaluate } from './charts';
import { q, type Recorder } from './trace';
import type { Dilution } from '../model/types';

export interface ChartDilution {
  degree: Dilution;
  /** Outside the plotted area of Fig. C.1; the boundary lines are extended (MD-003). */
  offChart: boolean;
  /** Within 5 % of a boundary line: the result is sensitive to the inputs. */
  nearBoundary?: 'high/medium' | 'medium/low';
  /** uw on each boundary at this Qc, m/s. */
  uwHighMedium: number;
  uwMediumLow: number;
}

const NEAR = 1.05;

/** Degree of dilution from Fig. C.1. Points exactly on a line take the less favourable side. */
export function chartDilution(qc: number, uw: number): ChartDilution {
  const uwHighMedium = evaluate(FIG_C1.highMedium, qc);
  const uwMediumLow = evaluate(FIG_C1.mediumLow, qc);
  const degree: Dilution = uw > uwHighMedium ? 'high' : uw > uwMediumLow ? 'medium' : 'low';
  const [qMin, qMax] = FIG_C1.qcRange;
  const [uMin, uMax] = FIG_C1.uwRange;
  const offChart = qc < qMin || qc > qMax || uw < uMin || uw > uMax;
  let nearBoundary: ChartDilution['nearBoundary'];
  const rHM = uw / uwHighMedium;
  const rML = uw / uwMediumLow;
  if (rHM > 1 / NEAR && rHM < NEAR) nearBoundary = 'high/medium';
  else if (rML > 1 / NEAR && rML < NEAR) nearBoundary = 'medium/low';
  return { degree, offChart, uwHighMedium, uwMediumLow, ...(nearBoundary ? { nearBoundary } : {}) };
}

export function recordChartDilution(qc: number, uw: number, rec: Recorder, label = 'Degree of dilution'): ChartDilution {
  const d = chartDilution(qc, uw);
  const notes: NonNullable<Parameters<Recorder['step']>[0]['notes']> = [];
  if (d.offChart) {
    notes.push({ kind: 'decision', text: 'The point lies outside the plotted range of Fig. C.1; the boundary lines are extended to classify it.', ref: { clause: 'C.3.5', md: 'MD-003' } });
  }
  if (d.nearBoundary) {
    notes.push({ kind: 'warning', text: `Within 5 % of the ${d.nearBoundary} boundary. Small changes in the inputs change the result.` });
  }
  rec.step({
    id: 'dilution.chart',
    label,
    ref: { clause: 'C.3.5', figure: 'C.1', md: 'MD-012' },
    inputs: { Qc: q(qc, 'm3/s'), uw: q(uw, 'm/s'), 'uw on high/medium line': q(d.uwHighMedium, 'm/s'), 'uw on medium/low line': q(d.uwMediumLow, 'm/s') },
    output: { symbol: 'dilution', text: capitalise(d.degree) },
    ...(notes.length ? { notes } : {}),
  });
  if (d.nearBoundary) {
    rec.issue({ code: 'dilution.near-boundary', severity: 'info', message: `The degree of dilution is within 5 % of the ${d.nearBoundary} boundary of Fig. C.1.`, ref: { clause: 'C.3.5', figure: 'C.1' } });
  }
  return d;
}

export const capitalise = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);
